import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getWorkTimeTypes } from "../services/workTimeBetaService";
import { WorkTimeType } from "../models/workShitTimeSlot/workTimeType";
import { VehicleDriving } from "../models/vehicle/vehiclePosition";

export type PrimaryFilter = "all" | "working" | "notWorking";
export type SortOrder = "employeeName" | "registerNumber";

interface MonitorViewFiltersState {
    primaryFilter: PrimaryFilter;
    workTimeTypeIds: (string | null)[];
    working: boolean;
    noWork: boolean;
    noWorkTime: boolean;
    workTimeTypes: WorkTimeType[] | null;
    workTimeTypesLoading: boolean;
    workTimeTypesError: string | null;
    selectedVehicleGroupIds: string[];
    selectedVehicleStatusFilter: number | "All";
    selectedTelematicsFilters: number[];
    showWorkingWithoutPhase: boolean;
    sortOrder: SortOrder;
}

const initialState: MonitorViewFiltersState = {
    primaryFilter: "all",
    workTimeTypeIds: [],
    working: true,
    noWork: true,
    noWorkTime: true,
    workTimeTypes: null,
    workTimeTypesLoading: false,
    workTimeTypesError: null,
    selectedVehicleGroupIds: [],
    selectedVehicleStatusFilter: "All",
    selectedTelematicsFilters: [
        VehicleDriving.Stopped,
        VehicleDriving.Moving,
        VehicleDriving.Driving,
        VehicleDriving.Inactive,
    ],
    showWorkingWithoutPhase: true,
    sortOrder: "registerNumber" as SortOrder,
};

export const fetchWorkTimeTypes = createAsyncThunk(
    "monitorViewFilters/fetchWorkTimeTypes",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getWorkTimeTypes();
            return response;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const monitorViewFiltersSlice = createSlice({
    name: "monitorViewFilters",
    initialState,
    reducers: {
        setPrimaryFilter(state, action: PayloadAction<PrimaryFilter>) {
            state.primaryFilter = action.payload;
        },
        setWorkTimeTypeIds(state, action) {
            state.workTimeTypeIds = action.payload;
        },
        toggleWorkTimeTypeId(state, action) {
            const { id, checked } = action.payload;
            if (checked) {
                state.workTimeTypeIds.push(id);
            } else {
                state.workTimeTypeIds = state.workTimeTypeIds.filter(
                    (workTimeTypeId) => workTimeTypeId !== id
                );
            }
        },
        setWorking(state, action) {
            state.working = action.payload;
        },
        setNoWork(state, action) {
            state.noWork = action.payload;
        },
        setNoWorkTime(state, action) {
            state.noWorkTime = action.payload;
        },
        setSelectedVehicleGroupIds(state, action) {
            state.selectedVehicleGroupIds = action.payload;
        },
        setSelectedVehicleStatusFilter(state, action) {
            state.selectedVehicleStatusFilter = action.payload;
        },
        setSelectedTelematicsFilters(state, action: PayloadAction<number[]>) {
            state.selectedTelematicsFilters = action.payload;
        },
        toggleTelematicsFilter(state, action: PayloadAction<number>) {
            const status = action.payload;
            if (state.selectedTelematicsFilters.includes(status)) {
                state.selectedTelematicsFilters =
                    state.selectedTelematicsFilters.filter((s) => s !== status);
            } else {
                state.selectedTelematicsFilters.push(status);
            }
        },
        setShowWorkingWithoutPhase(state, action: PayloadAction<boolean>) {
            state.showWorkingWithoutPhase = action.payload;
        },
        setSortOrder(state, action: PayloadAction<SortOrder>) {
            state.sortOrder = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWorkTimeTypes.pending, (state) => {
                state.workTimeTypesLoading = true;
                state.workTimeTypesError = null;
            })
            .addCase(fetchWorkTimeTypes.fulfilled, (state, action) => {
                state.workTimeTypes = action.payload;
                state.workTimeTypesLoading = false;
            })
            .addCase(fetchWorkTimeTypes.rejected, (state, action) => {
                state.workTimeTypesError = action.payload as string;
                state.workTimeTypesLoading = false;
            });
    },
});

export const {
    setPrimaryFilter,
    setWorkTimeTypeIds,
    toggleWorkTimeTypeId,
    setWorking,
    setNoWork,
    setNoWorkTime,
    setSelectedVehicleGroupIds,
    setSelectedVehicleStatusFilter,
    setSelectedTelematicsFilters,
    toggleTelematicsFilter,
    setShowWorkingWithoutPhase,
    setSortOrder,
} = monitorViewFiltersSlice.actions;

export const monitorViewFiltersReducer = monitorViewFiltersSlice.reducer;
